import { Box, Divider, Flex, Text } from "@chakra-ui/react";
import React from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useRecoilValue } from "recoil";
import { FlatSheetKey, inflatedTabsFamily } from "@/features/line-sheet-sets/helpers/sheet-state";
import { CommissionType } from "@/features/line-sheet-sets/line-sheet-set.types";

interface Props {
  sheetKey: FlatSheetKey;
  exchangeCurrencyValue: number;
  selectedCurrency: string;
  checkedProductIds: number[];
  isFiltered: boolean;
  orderStatus: "ORDERED" | "CONFIRMED";
  // 라인시트세트 수수료. commissionType이 ORDER면 상품 단가에는 수수료가 빠져 있으므로
  // 총 주문금액(컨디션 반영 합계)에만 수수료를 반영한다.
  commission?: number | null;
  commissionType?: CommissionType | null;
}

export const OrderSheetTabBottomBar = ({
  sheetKey,
  exchangeCurrencyValue,
  selectedCurrency,
  checkedProductIds,
  isFiltered,
  orderStatus,
  commission,
  commissionType,
}: Props) => {

  const inflatedTabs = useRecoilValue(inflatedTabsFamily(sheetKey));

  const { t, tTitle, tCurrencyString, tNumberString } = useI18nHelper();

  const totalProductCount = inflatedTabs?.tabs[0]?.products?.length || 0;
  const filteredProductCount = inflatedTabs?.tabs[0]?.products?.filter(
    (product) => {
      return product.isFiltered
    }
  )?.length

  const totalQuantity = inflatedTabs?.tabs.map(
    (tab) => tab.products.reduce(
      (acc, product) => {
        const orderQuantityWithOptionList = orderStatus === "ORDERED" ? product.orderQuantityWithOptionList : product.newStockWithOptionList || [];
        const productTotalQuantity = orderQuantityWithOptionList.reduce((sum, option) => sum + (option.quantity || 0), 0);
        return acc + productTotalQuantity;
      },
      0
    )
  ).reduce((sum, qty) => sum + qty, 0) || 0;

  // 합계는 화면에 보이는 단가(소수점 둘째 자리)를 그대로 더한 값과 일치해야 한다.
  // 그래서 수량을 곱하기 전에 단가부터 둘째 자리로 반올림한다.
  const round2 = (n: number) => Math.round(n * 100) / 100;

  const totalAmount = (inflatedTabs?.tabs.map(
    (tab) => tab.products.reduce(
      (acc, product) => {
        const priceAmount = product.priceAdjustedBy.by === "MARKUP" ? product.costPrice?.amount : product.retailPrice?.amount;
        const orderQuantityWithOptionList = orderStatus === "ORDERED" ? product.orderQuantityWithOptionList : product.newStockWithOptionList || [];
        const productTotalQuantity = orderQuantityWithOptionList.reduce((sum, option) => sum + (option.quantity || 0), 0);
        let productTotalAmount = 0;
        if (typeof priceAmount === "number") {
          productTotalAmount = round2(priceAmount) * productTotalQuantity;
        }
        return acc + productTotalAmount;
      },
      0
    )
  ).reduce((sum, amt) => sum + amt, 0) || 0) * exchangeCurrencyValue;

  const totalAmountWithCondition = (inflatedTabs?.tabs.map(
    (tab) => tab.products.reduce(
      (acc, product) => {
        // 행 확정금액(sheet-flattener의 confirmedPriceInfo)과 동일한 우선순위로
        // 단가를 도출해야 합계가 행 합과 일치한다.
        // confirmedPrice(수수료/IVA 반영)를 쓰고, 확정 단가가 없을 때만 supplyPrice로 폴백.
        let priceAmount: number | undefined;
        if (orderStatus === "ORDERED") {
          priceAmount = product.supplyPrice?.amount;
        } else {
          priceAmount = product.confirmedPrice?.amount;
          if (!priceAmount && product.supplyPrice) {
            priceAmount = product.supplyPrice.amount;
          }
        }
        const orderQuantityWithOptionList = orderStatus === "ORDERED" ? product.orderQuantityWithOptionList : product.newStockWithOptionList || [];
        const productTotalQuantity = orderQuantityWithOptionList.reduce((sum, option) => sum + (option.quantity || 0), 0);

        let productTotalAmount = 0;
        if (typeof priceAmount === "number") {
          // 단가를 소수점 둘째 자리로 먼저 반올림한 뒤 수량을 곱한다 (행 합계 및 화면 단가와 일치).
          productTotalAmount = round2(priceAmount) * productTotalQuantity;
        }
        return acc + productTotalAmount;
      },
      0
    )
  ).reduce((sum, amt) => sum + amt, 0) || 0) * exchangeCurrencyValue
    // 수수료 계산 단위가 ORDER면 합계에 수수료를 반영(상품 단가에는 미반영되어 있음)
    * (commissionType === "ORDER" && commission ? 1 + commission / 100 : 1);

  return (
    // 그리드 안에서는 ag-theme 래퍼([class*=ag-theme-])의 13px·Inter·antialiased 를
    // 물려받아 보였다. 갤러리처럼 그 밖에 놓여도 똑같이 보이도록 여기서 직접 정한다.
    // 세로 flex 의 자식으로 놓이면(갤러리) 목록이 넘칠 때 같이 눌려 48px 이 안 나온다 —
    // 줄어들지 않게 못 박는다.
    <Box
      flexShrink={0}
      fontFamily={"Inter"}
      fontSize={"13px"}
      sx={{ WebkitFontSmoothing: "antialiased" }}
      style={{
        borderTop: "0.5px solid #D4D4D1",
        height: "48px",
        alignItems: "center",
      }}
    >
      <Flex justifyContent={"space-between"} padding={"8px 16px"} alignItems={"center"} height={"100%"}>
        <Flex height={"100%"} alignItems={"center"}>
          <Text
            color={"#6F6F6C"}
            fontWeight={"500"}
            lineHeight="1.2"
          >
            {tTitle("total_product_count")}
          </Text>
          <Text
            color={"#444444"}
            fontWeight={"700"}
            marginLeft={"4px"}
            lineHeight="1.2"
          >
            {tNumberString(totalProductCount)}
          </Text>
          <Text
            color={"#6F6F6C"}
            fontWeight={"500"}
            marginLeft={"1px"}
            lineHeight="1.2"
          >
            {t("quantity")}
          </Text>
          {
            checkedProductIds?.length > 0 &&
            <>
              <Text
                color={"#6F6F6C"}
                fontWeight={"500"}
                marginLeft={"4px"}
                lineHeight="1.2"
              >
                /
              </Text>
              <Text
                color={"#444444"}
                fontWeight={"700"}
                marginLeft={"4px"}
                lineHeight="1.2"
              >
                {tNumberString(checkedProductIds?.length || 0)}
              </Text>
              <Text
                color={"#6F6F6C"}
                fontWeight={"500"}
                marginLeft={"1px"}
                lineHeight="1.2"
              >
                {t("common\:search.selected")}
              </Text>
            </>
          }
          {
            isFiltered &&
            <>
              <Text
                color={"#6F6F6C"}
                fontWeight={"500"}
                marginLeft={"4px"}
                lineHeight="1.2"
              >
                ({tTitle("search_filter")}
              </Text>
              <Text
                color={"#444444"}
                fontWeight={"700"}
                marginLeft={"4px"}
                lineHeight="1.2"
              >
                {tNumberString(filteredProductCount || 0)}
              </Text>
              <Text
                color={"#6F6F6C"}
                fontWeight={"500"}
                marginLeft={"1px"}
                lineHeight="1.2"
              >
                {t("quantity")})
              </Text>
            </>
          }
        </Flex>
        <Flex height={"100%"} alignItems={"center"} >
          <Text
            color={"#6F6F6C"}
            lineHeight="1.2"
          >
            {orderStatus === "ORDERED" ? tTitle("total_qty") : tTitle("total_confirmed_quantity")}
          </Text>
          <Text
            color={"#444444"}
            fontWeight={"bold"}
            marginLeft={"6px"}
            lineHeight="1.2"
          >
            {tNumberString(totalQuantity)}
          </Text>
          <Divider
            orientation={"vertical"}
            marginX={"8px"}
            border={"1px solid #D4D4D1"}
            height={"70%"}
          />
          <Text
            color={"#6F6F6C"}
            lineHeight="1.2"
          >
            {orderStatus === "ORDERED" ? tTitle("total_amount") : tTitle("total_confirmed_amount")}
          </Text>
          <Text
            color={"#1272EF"}
            fontWeight={"600"}
            marginLeft={"6px"}
            lineHeight="1.2"
          >
            {tCurrencyString(
              totalAmount,
              {
                type: "SYMBOL",
                code: selectedCurrency,
              }
            )}
          </Text>
          <Divider
            orientation={"vertical"}
            marginX={"8px"}
            border={"1px solid #D4D4D1"}
            height={"70%"}
          />
          <Text
            color={"#6F6F6C"}
            lineHeight="1.2"
          >
            {orderStatus === "ORDERED" ? tTitle("total_amount_condition") : tTitle("total_confirmed_amount_condition")}
          </Text>
          <Text
            color={"#1272EF"}
            fontWeight={"600"}
            marginLeft={"6px"}
            lineHeight="1.2"
          >
            {tCurrencyString(
              totalAmountWithCondition,
              {
                type: "SYMBOL",
                code: selectedCurrency,
              }
            )}
          </Text>
        </Flex>
      </Flex>
    </Box>
  )
}